/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as SemanticForm } from 'semantic-ui-react';
import { useMutation } from '@apollo/client';
import _ from 'lodash';

import {
  ErrorMessage,
  Field,
  withFormik,
} from 'formik';

import { ErrorHandler, InputFormField } from '@playbookux/playbook-web-core';
import { CREATE_TEMPLATE } from '../../../core/graphql/templates';
import cloneQuestionsWithOptions from '../../../core/utils/clone-questions-with-options';
import typeTemplate from '../../../core/constants/templateType';
import { remapAudienceOptions, remapScreenerAudienceTemplate } from '../../../core/utils/remap-audience-template';

import {
  CancelButton,
  SubmitButton,
  ContentField,
  ContentActions,
  ContentError,
  ContainerText,
  Modal,
  Content,
  ContentRow,
  Title,
  Error,
  Text,
} from './styles';

import schema from './schema';

const TEXTS = {
  audience: {
    title: 'Save Audience',
    content: 'Saving this audience as a template allows you to quickly launch studies in'
      + ' the future. You can view and edit this audience by clicking Account > Template Library',
    labelName: 'Audience name',
    labelDescription: 'Audience description',
  },
  test: {
    title: 'Save as Template',
    content: 'Saving this test script as a template allows you to quickly launch studies in'
      + ' the future. You can view and edit this template by clicking Account >'
      + ' Template Library',
    labelName: 'Template name',
    labelDescription: 'Template description',
  },
};

const SaveTemplateModal = (props) => {
  const {
    isOpen,
    onClose,
    handleSubmit,
    setValues,
    values,
    templateType,
    setTouched,
  } = props;

  const [loading, setLoading] = useState(false);

  const [
    createTemplate,
    {
      error,
    },
  ] = useMutation(CREATE_TEMPLATE);

  useEffect(() => {
    setValues({
      ...values,
      setLoading,
      createTemplate,
      error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTemplate, error, isOpen, setLoading]);

  const handleClose = () => {
    setValues({
      templateName: '',
      templateDescription: '',
    });
    setTouched({
      templateName: false,
      templateDescription: false,
    });
    onClose();
  };

  return (
    <Modal
      size="tiny"
      open={isOpen}
      closeOnEscape
      closeOnDimmerClick
      onClose={handleClose}
    >
      <Content>
        <SemanticForm>
          <ContentRow>
            <Title>
              {TEXTS[templateType].title}
            </Title>
          </ContentRow>
          <ContentRow>
            <ContainerText>
              {TEXTS[templateType].content}
            </ContainerText>
          </ContentRow>
          <ContentRow>
            <ContentField>
              <Text as="h2">{TEXTS[templateType].labelName}</Text>
              <Field
                name="templateName"
                placeholder="Enter name"
                component={InputFormField}
              />
              <ErrorMessage component={Error} name="templateName" />
            </ContentField>
          </ContentRow>
          <ContentRow>
            <ContentField>
              <Text as="h2">{TEXTS[templateType].labelDescription}</Text>
              <Field
                name="templateDescription"
                placeholder="Enter description"
                component={InputFormField}
              />
            </ContentField>
          </ContentRow>
        </SemanticForm>
        {error
          && (
            <ContentError>
              <ErrorHandler
                error={error}
                defaultMessage="Error while creating the template"
                revealError
              />
            </ContentError>
          )}
        <ContentActions>
          <CancelButton onClick={handleClose} disabled={loading}>
            Cancel
          </CancelButton>
          <SubmitButton loading={loading} onClick={handleSubmit} type="submit">
            Save
          </SubmitButton>
        </ContentActions>
      </Content>
    </Modal>
  );
};

SaveTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  values: PropTypes.shape({}).isRequired,
  templateType: PropTypes.string,
  setTouched: PropTypes.func.isRequired,
};

SaveTemplateModal.defaultProps = {
  isOpen: false,
  templateType: 'audience',
};

export default compose(
  withFormik({
    validationSchema: schema,
    mapPropsToValues: () => ({
      templateName: '',
      templateDescription: '',
    }),
    handleSubmit: async (values, { props, setStatus, resetForm }) => {
      const {
        questions,
        companyId,
        onClose,
        templateContext,
        projectId,
        segmentId,
        refetchProject,
        templateType,
        device,
        operatingSystem,
        audienceAttributes: attributesAudience,
        screener,
        participantsLanguage,
        otherTesterRequirements,
      } = props;

      const {
        createTemplate,
        templateName,
        templateDescription,
        setLoading,
      } = values;

      setStatus({
        error: null,
      });

      setLoading(true);

      try {
        let newTasks = [];
        let newQuestionsAttributes = [];
        let audienceAttributesAttributes = [];
        let language = null;
        let testerRequirements = null;
        let projectIdToSend = null;
        let segmentIdToSend = null;

        if (templateType === typeTemplate.TEMPLATE_TEST) {
          const newQuestions = _.chain(questions)
            .map((question) => ({
              ...question,
              range: question.questionOptions ? question.questionOptions.length : 0,
            }))
            .sortBy('questionOrder')
            .value();

          newTasks = cloneQuestionsWithOptions(newQuestions);
          // Sanitizing task object to remove imageUrl and videoUrl.
          newTasks.forEach((task) => {
            delete task.imageUrl;
            delete task.videoUrl;
          });

          newQuestionsAttributes = newTasks;
          projectIdToSend = projectId;
          segmentIdToSend = null;
        }

        if (templateType === typeTemplate.TEMPLATE_AUDIENCE) {
          audienceAttributesAttributes = remapAudienceOptions(attributesAudience);
          if (!_.isEmpty(screener)) {
            newTasks = remapScreenerAudienceTemplate(screener.questions);
          }

          language = participantsLanguage;
          testerRequirements = otherTesterRequirements;

          newQuestionsAttributes = newTasks.map((task) => {
            const options = _.omit(task, ['treeTestNodes', 'followUpQuestions', 'screenerRatingPercentage', 'questionOptionsAttributes']);

            const questionOptionsAttributes = task.questionOptionsAttributes.map((option) => _.omit(option, ['optionRatingPercentage']));

            return ({
              ...options,
              treeTestNodeIds: task.treeTestNodes.map((node) => node.id),
              followUpQuestionsAttributes: task.followUpQuestions,
              questionOptionsAttributes,
            });
          });
          projectIdToSend = null;
          segmentIdToSend = segmentId;
        }

        await createTemplate({
          variables: {
            projectId: projectIdToSend,
            segmentId: segmentIdToSend,
            attributes: {
              companyId,
              name: templateName,
              description: templateDescription,
              templateContext,
              templateType,
              audienceAttributesAttributes,
              language,
              device,
              operatingSystem,
              testerRequirements,
              surveyAttributes: {
                surveyType: templateType === typeTemplate.TEMPLATE_TEST ? 'session' : 'screener',
                questionsAttributes: newQuestionsAttributes,
              },
            },
          },
        });

        await refetchProject();

        resetForm();
        setLoading(false);

        onClose();
      } catch (err) {
        setStatus({ error: err });
        setLoading(false);
      }
    },
  }),
)(SaveTemplateModal);
